import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Tag, Table, Modal } from "antd";
import { ColumnType } from "antd/lib/table";
import { SearchOutlined } from "@ant-design/icons"; // Import the icon you're using
import StatusCard from "./StatusCard";
import { Link } from "react-router-dom";
import "./Components.css"; // Adjust the path based on your file structure
import { Input, Button, Checkbox } from "antd";
import Spinner from "./Spinner";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Papa from "papaparse";
import { Card, Statistic, Row, Col, Alert } from "antd";
import { Select } from "antd";
import axios from "axios";
import { getEndpoint } from "../utils/endpoints";

const STATUS_FILTER_MAP: Record<string, string> = {
  translated: "translated",
  "already translated": "translated",
  translating: "translating",
  ready: "ready",
  "to be translated": "ready",
  downloaded: "ready",
  new: "ready",
  "": "ready",
  error: "error",
};

const getStatusFilterKey = (status?: string | null) => {
  const normalized = (status ?? "").toString().trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(STATUS_FILTER_MAP, normalized)
    ? STATUS_FILTER_MAP[normalized]
    : normalized || "ready";
};

const { Option } = Select;
const { TextArea } = Input;
interface ChildProps {}

const DocumentDetails: React.FC<ChildProps> = () => {
  DocumentDetails.displayName = "DocumentDetails";

  const { document_uuid } = useParams(); // Get parameters from the route
  const navigate = useNavigate();
  const [documentJobName, setDocumentJobName] = useState<any>(null);

  const [documentData, setDocumentData] = useState<any>(null);
  const [filteredDocumentData, setFilteredDocumentData] = useState<any>([]);

  const [documentName, setDocumentName] = useState<any>(null);
  const [documentStats, setDocumentStats] = useState<any>({ translated: 0 });
  const [alertMessage, setAlertMessage] = useState("");
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [documentTotal, setDocumentTotal] = useState<number>(0);
  const [selectedCards, setSelectedCards] = useState<string[]>([
    "ready",
    "translating",
    "translated",
    "error",
  ]);
  const allStatuses = [
    "ready",
    "to be translated",
    "translating",
    "translated",
    "error",
  ];

  const [isLoading, setIsLoading] = useState(false);

  const [editingRowId, setEditingRowId] = useState<string | null>(null);

  const [statusUpdates, setStatusUpdates] = useState<Record<string, string>>(
    {}
  );
  const [instructions, setInstructions] = useState<string>("");
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isAIModalVisible, setIsAIModalVisible] = useState<boolean>(false);
  // --- PE term-swap (find/replace with gender/number agreement) ---
  const [swapSource, setSwapSource] = useState<string>("");
  const [swapTarget, setSwapTarget] = useState<string>("");
  const [swapRows, setSwapRows] = useState<any[]>([]);
  const [swapModalOpen, setSwapModalOpen] = useState<boolean>(false);
  const [swapLoading, setSwapLoading] = useState<boolean>(false);
  const [swapSelected, setSwapSelected] = useState<Record<string, boolean>>({});
  const [swapEdits, setSwapEdits] = useState<Record<string, string>>({});
  const [swapShowAll, setSwapShowAll] = useState<boolean>(false);
  const [swapSuggestOpen, setSwapSuggestOpen] = useState<boolean>(false);
  const [swapSuggestions, setSwapSuggestions] = useState<any[]>([]);
  const [swapSuggestLoading, setSwapSuggestLoading] = useState<boolean>(false);
  const [sourceUpdates, setSourceUpdates] = useState<Record<string, string>>(
    {}
  );
  const [targetUpdates, setTargetUpdates] = useState<Record<string, string>>(
    {}
  );
  const [reviewUpdates, setReviewUpdates] = useState<Record<string, string>>({});

  const getDocumentDetails = async (documentUuid: string) => {
    const response = await fetch(`${getEndpoint('BACKEND')}/getDocumentsDetails/v2/${documentUuid}/` , {
      method: 'GET',
      mode: 'cors',  // Ensure CORS mode
      headers: {
        'Accept': 'application/json', // Specify the accepted content type
        // Add other headers if needed
      }
    });

    if (response.status !== 200) {
      throw new Error('Network response was not ok');
    }
    return response.json();
  };

  const initiateEdit = (
    rowId: string,
    status: string,
    target: string,
    source: string,
    review: string
  ) => {
    setEditingRowId(rowId);
    setStatusUpdates((prevStatusUpdates) => ({
      ...prevStatusUpdates,
      [rowId]: status,
    }));
    setTargetUpdates((prevTargetUpdates) => ({
      ...prevTargetUpdates,
      [rowId]: target,
    }));
    setSourceUpdates((prevSourceUpdates) => ({
      ...prevSourceUpdates,
      [rowId]: source,
    }));
    setReviewUpdates((prevReviewUpdates) => ({
      ...prevReviewUpdates,
      [rowId]: review,
    }));
  };

  const saveChanges = async (rowId: string) => {
    const updatedData = [...documentData];
    const dataIndex = updatedData.findIndex((item) => item.row_number === rowId);
    if (dataIndex === -1) {
      setEditingRowId(null);
      return;
    }
    const updatedRow = updatedData[dataIndex];
    if (!updatedRow) {
      setEditingRowId(null);
      return;
    }
    const nextStatus = statusUpdates[rowId];
    if (nextStatus) {
      updatedRow.status = nextStatus;
      updatedRow.row_status = nextStatus;
    }
    updatedRow.target = targetUpdates[rowId] || updatedRow.target;
    updatedRow.source = sourceUpdates[rowId] || updatedRow.source;
    updatedRow.review = reviewUpdates[rowId] || updatedRow.review;

    setLoading(true);
    try {
      updatedRow.document_uuid = document_uuid;
      updatedRow.job_name = documentJobName;
      updatedRow.document_name = documentName;
      await axios.post(
        `${getEndpoint('BACKEND')}/updateDocumentsDetails/`,
        updatedRow
      );
      setAlertMessage("Record successfully updated");
      handleRefresh();
    } catch (error) {
      setAlertMessage("Failed to update the record");
      console.error("Failed to update the record:", error);
    } finally {
      setLoading(false);
    }
    setEditingRowId(null);
  };

  // PE term-swap: preview replacing a source term's rendering across the whole
  // document, with automatic gender/number re-agreement of linked elements.
  // Audit the document for terms worth cleaning up. The audit runs async on the
  // backend (chunked LLM pass, minutes on big docs), so we start it then poll.
  const handleSuggest = async () => {
    setSwapSuggestLoading(true);
    try {
      await axios.post(`${getEndpoint("BACKEND")}/pe/termSwap/suggest/${document_uuid}`);
      const startedAt = Date.now();
      const poll = async () => {
        try {
          const r = await axios.get(
            `${getEndpoint("BACKEND")}/pe/termSwap/suggestions/${document_uuid}`
          );
          const st = r.data?.status;
          if (st === "ready") {
            setSwapSuggestions(r.data?.suggestions || []);
            setSwapSuggestOpen(true);
            setSwapSuggestLoading(false);
          } else if (st === "error") {
            setAlertMessage("Errore nel calcolo dei suggerimenti.");
            setIsAlertVisible(true);
            setSwapSuggestLoading(false);
          } else if (Date.now() - startedAt > 5 * 60 * 1000) {
            setAlertMessage("Suggerimenti: tempo scaduto, riprova.");
            setIsAlertVisible(true);
            setSwapSuggestLoading(false);
          } else {
            setTimeout(poll, 4000);
          }
        } catch (e) {
          setTimeout(poll, 4000); // transient error -> keep polling
        }
      };
      setTimeout(poll, 4000);
    } catch (error) {
      setAlertMessage("Errore nell'avvio dei suggerimenti.");
      setIsAlertVisible(true);
      setSwapSuggestLoading(false);
      console.error("handleSuggest error:", error);
    }
  };

  // Pick a suggestion -> pre-fill the swap bar (user then runs Anteprima).
  const pickSuggestion = (s: any) => {
    setSwapSource(s.source_term || "");
    setSwapTarget(s.suggested_canonical || "");
    setSwapSuggestOpen(false);
  };

  const handleSwapPreview = async () => {
    if (!swapSource.trim() || !swapTarget.trim()) return;
    setSwapLoading(true);
    try {
      const resp = await axios.post(
        `${getEndpoint("BACKEND")}/pe/termSwap/preview/${document_uuid}`,
        { source_term: swapSource.trim(), new_target: swapTarget.trim(), include_unchanged: swapShowAll }
      );
      // In show-all mode the backend returns unchanged occurrences too; take all rows.
      const rows = resp.data?.rows || [];
      const sel: Record<string, boolean> = {};
      const edits: Record<string, string> = {};
      rows.forEach((r: any) => {
        sel[String(r.row_number)] = !!r.changed; // pre-select only rows that actually change
        edits[String(r.row_number)] = r.after; // seed editable field with the suggestion
      });
      setSwapRows(rows);
      setSwapSelected(sel);
      setSwapEdits(edits);
      setSwapModalOpen(true);
    } catch (error) {
      setAlertMessage("Errore durante l'anteprima della sostituzione.");
      setIsAlertVisible(true);
      console.error("handleSwapPreview error:", error);
    } finally {
      setSwapLoading(false);
    }
  };

  const handleSwapApply = async () => {
    const updates = swapRows
      .filter((r: any) => swapSelected[String(r.row_number)])
      .map((r: any) => ({
        row_number: r.row_number,
        after: swapEdits[String(r.row_number)] ?? r.after,
      }));
    if (updates.length === 0) { setSwapModalOpen(false); return; }
    setSwapLoading(true);
    try {
      const resp = await axios.post(
        `${getEndpoint("BACKEND")}/pe/termSwap/apply/${document_uuid}`,
        { updates }
      );
      setAlertMessage(`Sostituzione applicata a ${resp.data?.applied ?? updates.length} righe.`);
      setIsAlertVisible(true);
      setSwapModalOpen(false);
      handleRefresh();
    } catch (error) {
      setAlertMessage("Errore durante l'applicazione della sostituzione.");
      setIsAlertVisible(true);
      console.error("handleSwapApply error:", error);
    } finally {
      setSwapLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    if (document_uuid) {
      const fetchData = async () => {
        try {
          const data = await getDocumentDetails(document_uuid);
  
          setDocumentData(
            Array.isArray(data?.document_rows) ? data.document_rows : []
          );
          setDocumentName(data?.document_name);
          setDocumentJobName(data?.job_name);
          setDocumentStats(data?.document_count || {});
  
          let total = 0;
          if (data?.document_count) {
            for (const key in data.document_count) {
              if (Object.prototype.hasOwnProperty.call(data.document_count, key)) {
                total += data.document_count[key] || 0;
              }
            }
          }
          setDocumentTotal(total);
  
          setIsLoading(false);
        } catch (error) {
          console.error("Error fetching document details:", error);
          setIsLoading(false);
        }
      };
  
      fetchData();
    }
  }, [document_uuid]);
  
  useEffect(() => {
    if (documentData) {
      const filteredData = documentData.filter((row: any) =>
        selectedCards.includes(getStatusFilterKey(row.status))
      );
      setFilteredDocumentData(filteredData);
    }
  }, [selectedCards, documentData]);
  
  

  const handleCancel = () => {
    setIsModalVisible(false);
    setIsAIModalVisible(false);

  };

  const translateAgainDirect = async (rowNumber: string) => {
    // Find the index of the row in the documentData array
    const rowIndex = documentData.findIndex((item: any) => item.row_number.toString() === rowNumber);
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${getEndpoint('BACKEND')}/translateDocument/${document_uuid}`,
        {
          force: true,
          rownum: rowIndex,  // Use array index instead of row_number
        }
      );

      if (response.status === 200) {
        setAlertMessage("Translation initiated successfully");
        handleRefresh(); // Refresh the table to show updated data
      } else {
        setAlertMessage("Failed to initiate translation");
      }
    } catch (error) {
      console.error("Error initiating translation:", error);
      setAlertMessage("Error initiating translation");
    } finally {
      setIsAlertVisible(true);
      setIsLoading(false);
    }
  };

  const columns: ColumnType<any>[] = [
    {
      title: "Row ID",
      dataIndex: "row_number",
      key: "row_id",
      width: 80, // <--- accorcia larghezza

      sorter: (a, b) => parseInt(a.row_id) - parseInt(b.row_id),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Filter by text"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Confirm
          </Button>
          {clearFilters && ( // Check if clearFilters is defined before using it
            <Button
              onClick={() => clearFilters()}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          )}
        </div>
      ),
      onFilter: (value, record) => {
        const sourceText = record.source ?? '';  // usa ?? per catturare null e undefined
        const filterValue = String(value);
        const trimmedValue = filterValue.trim();
        
        // Se filtro vuoto/solo spazi, mostra solo record vuoti, null o undefined
        if (trimmedValue === '') {
          return !sourceText || sourceText.trim() === '';
        }
        // Altrimenti cerca il testo
        return sourceText.includes(filterValue);
      },
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      filtered: true,
      sorter: (a, b) => {
        const aSource = a.source || '';
        const bSource = b.source || '';
        return aSource.localeCompare(bSource);
      },
      sortDirections: ["ascend", "descend"],
      render: (source: any, record: any) => {
        if (editingRowId === record.row_number) {
          return (
            <Input.TextArea
              autoSize={{ minRows: 4 }}
              value={sourceUpdates[record.row_number] || source}
              onChange={(e) => {
                const value = e.target.value;
                setSourceUpdates((prev) => ({
                  ...prev,
                  [record.row_number]: value,
                }));
                setStatusUpdates((prev) => ({
                  ...prev,
                  [record.row_number]: "ready",
                }));
              }}
            />
          );
        } else {
          return <div dangerouslySetInnerHTML={{ __html: source }} />;
        }
      },
    },
    {
      title: "Target",
      dataIndex: "target",
      key: "target",
      
      render: (target: any, record: any) => {
        if (editingRowId === record.row_number) {
          return (
            <Input.TextArea
              autoSize={{ minRows: 4 }}
              value={targetUpdates[record.row_number] || target}
              onChange={(e) => {
                const value = e.target.value;
                setTargetUpdates((prev) => ({
                  ...prev,
                  [record.row_number]: value,
                }));
                setStatusUpdates((prev) => ({
                  ...prev,
                  [record.row_number]: "translated",
                }));
              }}
            />
          );
        } else {
          return <div dangerouslySetInnerHTML={{ __html: target }} />;
        }
      },

      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Filter by target content"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Confirm
          </Button>
          {clearFilters && ( // Check if clearFilters is defined before using it
            <Button
              onClick={() => clearFilters()}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          )}
        </div>
      ),
      onFilter: (value, record) => {
        const targetText = record.target ?? '';  // usa ?? per catturare null e undefined
        const filterValue = String(value);
        const trimmedValue = filterValue.trim();
        
        // Se filtro vuoto/solo spazi, mostra solo record vuoti, null o undefined
        if (trimmedValue === '') {
          return !targetText || targetText.trim() === '';
        }
        // Altrimenti cerca il testo
        return targetText.includes(filterValue);
      },
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      filtered: true,
      sorter: (a, b) => {
        const aTarget = a.target || '';
        const bTarget = b.target || '';
        return aTarget.localeCompare(bTarget);
      },
      sortDirections: ["ascend", "descend"],
        },
        /*{
      title: "Reviewed",
      dataIndex: "review",
      key: "review",
      
      render: (reviewed: any, record: any) => {
        if (editingRowId === record.row_number) {
          return (
        <ReactQuill
          theme="snow"
          value={reviewUpdates[record.row_number] || reviewed}
          style={{ minHeight: "100px", height: "auto", overflow: "hidden" }}
          onChange={(value) => {
            setReviewUpdates((prev) => ({
          ...prev,
          [record.row_number]: value,
            }));
            setStatusUpdates((prev) => ({
          ...prev,
          [record.row_number]: "translated",
            }));
          }}
        />
          );
        } else {
          return <div dangerouslySetInnerHTML={{ __html: reviewed }} />;
        }
      },

      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Filter by target content"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Confirm
          </Button>
          {clearFilters && ( // Check if clearFilters is defined before using it
            <Button
              onClick={() => clearFilters()}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          )}
        </div>
      ),
      onFilter: (value, record) => record.reviewed.includes(value),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      filtered: true,
      sorter: (a, b) => a.reviewed.localeCompare(b.reviewed),
      sortDirections: ["ascend", "descend"],
    },*/  
    {
      title: "Row Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Ready", value: "ready" },
        { text: "Translating", value: "translating" },
        { text: "Translated", value: "translated" },
        { text: "Error", value: "error" },
        { text: "Already Translated", value: "already translated" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status: any, record: any) => {
        if (editingRowId === record.row_number) {
          return (
            <Select
              value={statusUpdates[record.row_number] || status}
              onChange={(newStatus) => {
                setStatusUpdates((prevStatusUpdates) => ({
                  ...prevStatusUpdates,
                  [record.row_number]: newStatus,
                }));
              }}
            >
              <Option value="translated">Translated</Option>
              <Option value="ready">Ready</Option>
              {/* Add other status options here */}
            </Select>
          );
        } else {
          let color = "green";
          switch (status) {
            case "translating":
              color = "geekblue";
              break;
            case "error":
              color = "volcano";
              break;
            case "ready":
              color = "yellow";
              break;
            // ... other statuses
          }

          return (
            <Tag color={color}>
              {(status === "downloaded" ? "ready" : status).toUpperCase()}
            </Tag>
          );
        }
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      width: 100,
      render: (_text: any, record: any) => (
        <div>
          {editingRowId === record.row_number ? (
            <>
              <a onClick={() => saveChanges(record.row_number)}>Save</a>
              <br></br>
              <a onClick={() => setEditingRowId(null)}>Cancel</a>
            </>
          ) : (
            <>
              <a
                
                onClick={() =>
                  initiateEdit(
                    record.row_number,
                    record.status,
                    record.target,
                    record.source,
                    record.review
                  )
                }
              >
                Edit
              </a>
              <br></br>
              <a 
                onClick={() => translateAgainDirect(record.row_number)}
                style={{ color: '#1890ff', cursor: 'pointer' }}
              >
                Translate Again
              </a>
            </>
          )}

        <a onClick={() => showModalAI(record.row_number)} style={{ display: 'none' }}>Review with AI</a>
            <Modal
            title="AI Review"
            visible={isAIModalVisible}
            onOk={() => translateAgain()}
            onCancel={handleCancel}
            >
            <TextArea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Enter your instructions here"
            />
            </Modal>
            
            
            <br></br>
            <a onClick={() => showModal(record.row_number)} style={{ display: 'none' }}>Translate again</a>
            <Modal
            title="Give instructions to the translator"
            visible={isModalVisible}
            onOk={() => translateAgain()}
            onCancel={handleCancel}
            >
            <TextArea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Enter your instructions here"
            />
            </Modal>

        </div>
      ),
    },
  ];

  const handleRefresh = async () => {
    setIsLoading(true);
    if (document_uuid) {
      try {
        const data = await getDocumentDetails(document_uuid); // Use the getDocumentDetails function you've created

        setDocumentData(
          Array.isArray(data?.document_rows) ? data.document_rows : []
        );
        setDocumentName(data?.document_name);
        setDocumentStats(data?.document_count);

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching document details:", error);
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    // Call handleRefresh initially
    handleRefresh();

    // Set up the interval to call handleRefresh every minute
    const intervalId = setInterval(() => {
      handleRefresh();
    }, 300000); // 300000 milliseconds = 1 minute

    // Clean up the interval when the component is unmounted
    return () => {
      clearInterval(intervalId);
    };
  }, []);


const [selectedRow, setSelectedRow] = useState<string | null>(null);

const showModal = (row_number: string) => {
  setSelectedRow(row_number); // Store row number
  setIsModalVisible(true);
};

const showModalAI = (row_number: string) => {
  setSelectedRow(row_number); // Store row number
  setIsAIModalVisible(true);
};


const reviewAI = async () => {
  
  if (!selectedRow) {
    console.error("No row selected for translation");
    return;
  }
  try {
    setIsLoading(true);
    const response = await axios.post(
      `${getEndpoint('BACKEND')}/reviewTranslation/${document_uuid}`,
      {
        mode: "sync",
        force: "true",
        rownum: Number(selectedRow),  // Rimossa conversione -1 perché selectedRow è già 0-based
      }
    );

    if (response.status === 200) {
      setAlertMessage("Review completed successfully");
    } else {
      setAlertMessage("Failed to initiate review");
    }
  } catch (error) {
    console.error("Error initiating review:", error);
    setAlertMessage("Error initiating reviee");
  } finally {
    setIsAlertVisible(true);
    setIsLoading(false);
  }
};

const translateAgain = async () => {
  setIsModalVisible(false);
  if (!selectedRow) {
    console.error("No row selected for translation");
    return;
  }
  // Find the index of the row in the documentData array
  const rowIndex = documentData.findIndex((item: any) => item.row_number.toString() === selectedRow);
  try {
    setIsLoading(true);
    const response = await axios.post(
      `${getEndpoint('BACKEND')}/translateDocument/${document_uuid}`,
      {
        mode: "sync",
        force: "true",
        rownum: rowIndex,  // Use array index instead of row_number
        special_instructions: instructions,
      }
    );

    if (response.status === 200) {
      setAlertMessage("Translation initiated successfully");
    } else {
      setAlertMessage("Failed to initiate translation");
    }
  } catch (error) {
    console.error("Error initiating translation:", error);
    setAlertMessage("Error initiating translation");
  } finally {
    setIsAlertVisible(true);
    setIsLoading(false);
  }
};

  const handleDownload = () => {
    if (documentData) {
      // Convert data to CSV format
      const csv = Papa.unparse(documentData, {
        quotes: true, // Add quotes around values if needed
        delimiter: ",", // Use comma as delimiter
        header: true // Include headers
      });
  
      // Create a Blob object with the CSV data
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  
      // Create a link element for downloading the file
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${documentName}_table_data.csv`; // Set the download filename
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click(); // Trigger the download
      document.body.removeChild(link); // Clean up
      URL.revokeObjectURL(url); // Release memory
    }
  };
  

  const handleDownloadBitext = () => {
    // Assuming tableRows is your data
    if (documentData) {
      const filteredData = documentData.map((row: any) => ({
        source: row.source,
        target: row.target,
      }));

      // Convert the filtered data to CSV
      const csvData = Papa.unparse(filteredData);

      // Create a blob of the data
      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });

      // Create a link element, use it to download the blob, and revoke the URL
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", documentName + "_bitext.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const toggleCardSelection = (status: string) => {
    const filterKey = getStatusFilterKey(status);
    setSelectedCards((prevSelected) =>
      prevSelected.includes(filterKey)
        ? prevSelected.filter((selectedStatus) => selectedStatus !== filterKey)
        : [...prevSelected, filterKey]
    );
  };

  return (
    <div>
      <div className="table-container">
        <h2>
          {" "}
          Document: {documentName}, Job Name:
          {documentJobName}
        </h2>
      </div>

      <div className="status-card-row">
        {allStatuses.map((status) => (
          <StatusCard
            key={status}
            title={status}
            className={
              selectedCards.includes(getStatusFilterKey(status))
                ? "selected-card"
                : "card"
            }
            count={documentStats?.[status] || 0}
            isLoading={isLoading}
            onClick={() => toggleCardSelection(status)}
          />
        ))}
        <StatusCard
          title="Total"
          isLoading={isLoading}
          className="card-total"
          count={documentTotal}
          onClick={() => {}}
        />
      </div>

      <Modal
        title="Termini da bonificare (clicca «Usa» per pre-riempire la barra)"
        visible={swapSuggestOpen}
        onCancel={() => setSwapSuggestOpen(false)}
        footer={null}
        width={900}
      >
        {swapSuggestions.length === 0 ? (
          <div>Nessun termine incoerente o in conflitto col glossario trovato. 👍</div>
        ) : (
          <div style={{ maxHeight: 460, overflowY: "auto" }}>
            {swapSuggestions.map((s: any, i: number) => (
              <div key={i} style={{ borderBottom: "1px solid #eee", padding: "8px 0", display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div>
                    <strong>{s.source_term}</strong>
                    <span style={{ color: "#999", marginLeft: 8 }}>({s.occurrences}×)</span>
                    <Tag color={s.from_glossary ? "geekblue" : "orange"} style={{ marginLeft: 8 }}>
                      {s.reason}
                    </Tag>
                  </div>
                  <div style={{ fontSize: 12, color: "#a00", marginTop: 2 }}>
                    rese: {(s.renderings || []).join("  ·  ")}
                  </div>
                  <div style={{ fontSize: 12, color: "#080", marginTop: 2 }}>
                    → canonico suggerito: <strong>{s.suggested_canonical}</strong>
                  </div>
                </div>
                <Button type="primary" size="small" onClick={() => pickSuggestion(s)}>
                  Usa
                </Button>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal
        title={`Sostituisci "${swapSource}" → "${swapTarget}" (concordanza automatica)`}
        visible={swapModalOpen}
        onCancel={() => setSwapModalOpen(false)}
        onOk={handleSwapApply}
        okText="Applica selezionate"
        cancelText="Annulla"
        confirmLoading={swapLoading}
        width={900}
      >
        {swapRows.length === 0 ? (
          <div>Nessuna riga da modificare.</div>
        ) : (
          <div style={{ maxHeight: 440, overflowY: "auto" }}>
            {swapRows.map((r: any) => (
              <div key={r.row_number} style={{ borderBottom: "1px solid #eee", padding: "6px 0" }}>
                <Checkbox
                  checked={!!swapSelected[String(r.row_number)]}
                  onChange={(e) =>
                    setSwapSelected({ ...swapSelected, [String(r.row_number)]: e.target.checked })
                  }
                >
                  <span style={{ color: "#999" }}>riga {r.row_number}</span>
                  {!r.changed && (
                    <Tag color="default" style={{ marginLeft: 6 }}>già ok</Tag>
                  )}
                  {r.flags && r.flags.length > 0 && (
                    <Tag color="orange" style={{ marginLeft: 6 }}>
                      {r.flags.join(",")}
                    </Tag>
                  )}
                </Checkbox>
                <div style={{ marginLeft: 24, color: "#555", whiteSpace: "pre-wrap", marginTop: 2 }}>
                  <span style={{ color: "#aaa" }}>source: </span>{r.source}
                </div>
                <div style={{ marginLeft: 24, color: "#a00", textDecoration: "line-through", whiteSpace: "pre-wrap" }}>
                  {r.before}
                </div>
                <Input.TextArea
                  value={swapEdits[String(r.row_number)] ?? r.after}
                  onChange={(e) =>
                    setSwapEdits({ ...swapEdits, [String(r.row_number)]: e.target.value })
                  }
                  autoSize
                  style={{ marginLeft: 24, width: "calc(100% - 24px)", color: "#080", borderColor: "#b7eb8f" }}
                />
                {(swapEdits[String(r.row_number)] ?? r.after) !== r.after && (
                  <span style={{ marginLeft: 24, fontSize: 11, color: "#888" }}>modificato a mano</span>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>

      <div className="table-container">
        <Table
          dataSource={filteredDocumentData}
          loading={isLoading}
          columns={columns}
          pagination={{ pageSize: 100 }}
        />
      </div>
      <div className="table-container">
        <Button
          onClick={handleRefresh}
          type="primary"
          style={{ margin: 16 }}
          disabled={isLoading}
        >
          Refresh table
        </Button>
        <Button
          onClick={handleDownload}
          type="primary"
          style={{ margin: 16 }}
          disabled={isLoading}
        >
          Download Table
        </Button>
        <Button
          onClick={handleDownloadBitext}
          type="primary"
          style={{ margin: 16 }}
          disabled={isLoading}
        >
          Download Bitext
        </Button>
      </div>

      {isAlertVisible && (
        <Alert
          message={alertMessage}
          type="success" // Change to "error" for error messages
          showIcon
          closable
          onClose={() => setIsAlertVisible(false)}
        />
      )}
    </div>
  );
};

export default DocumentDetails;
